import { cloneDeep, uniqBy } from 'lodash';

/**
 * Reads the display name of a size multiselect option, tolerating either `label` or `value`.
 * @param {{ label?: string, value?: string }} option
 * @returns {string|undefined}
 */
export const optionSizeName = (option) => option?.label || option?.value;

/**
 * Identity of a size multiselect option: its name scoped to the grading group that owns it.
 *
 * A size name is only unique WITHIN a grading group — two selected grading groups can each own a
 * size called `3YRS`, and they are different sizes. Keying selection by name alone makes those two
 * options indistinguishable, so deselecting `3YRS` in one group silently deselects it in every
 * other group and drops the name from the product-level size list, which then deletes the size
 * from the sibling group's document (BM-9510). Always compare options by this key, never by name.
 * @param {{ label?: string, value?: string, gradingGroupId?: string }} option
 * @returns {string} `<gradingGroupId>::<sizeName>`
 */
export const sizeOptionKey = (option) => `${String(option?.gradingGroupId ?? '')}::${optionSizeName(option) ?? ''}`;

/**
 * Reads size names off a mixed list of `{name}` objects and/or plain strings, dropping any
 * blank or nullish entry. Centralizes the `sizeRange?.name` read used to build ordering/seed
 * lists so a caller can never accidentally feed an unnamed entry into a gradings rebuild.
 * @param {Array<{name?: string}|string>|null|undefined} sizeRanges
 * @returns {string[]}
 */
export const sanitizeSizeNames = (sizeRanges) =>
  (Array.isArray(sizeRanges) ? sizeRanges : [])
    .map((sizeRange) => (typeof sizeRange === 'object' && sizeRange ? sizeRange.name : sizeRange))
    .filter((name) => name != null && String(name).trim() !== '');

/**
 * Reorders a POM's gradings entries to match `orderedNames`, guaranteeing the result never
 * ends up shorter than the array it started with. Blank/nullish names in `orderedNames` are
 * dropped before use, so they can never collide on a shared `undefined` key. Without this,
 * an ordering list whose items lack a `.name` (wrong shape, or a stale/not-yet-loaded source)
 * collapses every real grading entry into N duplicate `sizeName: undefined` placeholders — the
 * same lookup-by-Map-then-filter pattern was duplicated across the "Import Grading Group POM's"
 * flow and the size-visibility hide/show flow; this is the single guarded implementation both
 * now share (BM-9510).
 * @param {Array<{sizeName?: string}>|null|undefined} gradings
 * @param {Array<{name?: string}|string>|null|undefined} orderedNames
 * @returns {Array<{sizeName?: string}>}
 */
export const reorderGradingsSafely = (gradings, orderedNames) => {
  const existing = Array.isArray(gradings) ? gradings : [];
  const names = sanitizeSizeNames(orderedNames);
  const existingMap = new Map(existing.map((grad) => [grad.sizeName, grad]));
  const rebuilt = names.map((name) => existingMap.get(name)).filter((grad) => grad !== undefined);
  return rebuilt.length >= existing.length ? rebuilt : existing;
};

/**
 * Pure transform for a size-visibility change in the Spec tab. Deselecting a size only hides it
 * (adds it to `hiddenSizeNames`) — its gradings are never dropped, and `fullNames` (the group's full
 * size list) is unchanged so re-selecting is lossless. Only a brand-new size (absent from the group)
 * extends the full list and seeds gradings from the product grading library, defaulting to '0'.
 * Sample/base sizes in `protectedSampleSizes` are re-added to the visible set and can never be hidden.
 * @param {Object} params
 * @param {Array<{label?: string, value?: string, isSampleSize?: boolean}>} params.selected Currently selected options.
 * @param {string[]} [params.protectedSampleSizes] Sample/base size names that must stay visible.
 * @param {Array<{label?: string, value?: string, isSampleSize?: boolean}>} [params.sizeOptions] All size options.
 * @param {{sizeRanges?: Array}|null} [params.matchingSizeRange] Active tab's order grading group (source of the full list).
 * @param {Array<{name?: string, isSampleSize?: boolean}>} [params.localSizeRanges] Fallback full list.
 * @param {string[]} [params.sizeRange] Second fallback full list (names only).
 * @param {Array|null} [params.specData] POM rows whose gradings are retained/seeded.
 * @param {Array} [params.productPomLibraries] Product grading library used to seed a brand-new size.
 * @returns {{effectiveSelected: Array, visibleNames: string[], fullSizeData: Array, fullNames: string[], hiddenSizeNames: string[], brandNewSizes: string[], updatedSpecData: Array|null}}
 */
export const deriveSizeVisibility = ({
  selected,
  protectedSampleSizes = [],
  sizeOptions = [],
  matchingSizeRange = null,
  localSizeRanges = [],
  sizeRange = [],
  specData = null,
  productPomLibraries = [],
  activeGradingGroupId = '',
}) => {
  const extractSizeName = (item) => (typeof item === 'object' && item ? item.name : item);
  const options = Array.isArray(selected) ? selected : [];
  const rawSizeNames = options.map(optionSizeName);

  const missingSampleOptions = protectedSampleSizes
    .filter((sampleSize) => !rawSizeNames.includes(sampleSize))
    .map((sampleSize) => sizeOptions.find((opt) => opt.value === sampleSize || opt.label === sampleSize))
    .filter((opt) => opt !== undefined);
  const effectiveSelected = missingSampleOptions.length > 0 ? uniqBy([...options, ...missingSampleOptions], optionSizeName) : options;
  const visibleNames = uniqBy(effectiveSelected.map(optionSizeName));

  let fullSizeData = [];
  if (matchingSizeRange && Array.isArray(matchingSizeRange.sizeRanges)) {
    fullSizeData = matchingSizeRange.sizeRanges.map((res) => ({
      name: extractSizeName(res),
      isSampleSize: (typeof res === 'object' && res?.isSampleSize) || false,
    }));
  } else if (Array.isArray(localSizeRanges) && localSizeRanges.length > 0) {
    fullSizeData = localSizeRanges.map((res) => ({ name: extractSizeName(res), isSampleSize: res?.isSampleSize || false }));
  } else if (Array.isArray(sizeRange)) {
    fullSizeData = sizeRange.map((name) => ({ name, isSampleSize: false }));
  }

  const groupFullNames = fullSizeData.map((res) => res.name).filter(Boolean);
  const brandNewSizes = visibleNames.filter((name) => !groupFullNames.includes(name));
  brandNewSizes.forEach((newSizeName) => {
    const sizeOption = effectiveSelected.find((s) => optionSizeName(s) === newSizeName);
    fullSizeData.push({ name: newSizeName, isSampleSize: sizeOption?.isSampleSize || false });
  });

  const fullNames = fullSizeData.map((res) => res.name).filter(Boolean);
  const hiddenSizeNames = fullNames.filter((name) => !visibleNames.includes(name));
  /**
   * Each hidden size is stamped with the grading group it belongs to. Without that stamp a hidden
   * entry is just a name, and a name is only unique within a grading group — which is how hiding a
   * size in one group came to hide the same-named size in every other group (BM-9510).
   */
  const hiddenSizes = hiddenSizeNames.map((sizeName) => ({ sizeName, gradingGroupId: activeGradingGroupId }));

  let updatedSpecData = specData;
  if (Array.isArray(specData) && specData.length > 0) {
    updatedSpecData = specData.map((specItem) => {
      const clonedItem = cloneDeep(specItem);
      if (!Array.isArray(clonedItem.gradings)) {
        clonedItem.gradings = [];
      }
      brandNewSizes.forEach((newSizeName) => {
        const sizeExists = clonedItem.gradings.some((grad) => grad.sizeName === newSizeName);
        if (!sizeExists) {
          const sizeOption = effectiveSelected.find((s) => optionSizeName(s) === newSizeName);
          const isSampleSize = sizeOption?.isSampleSize || false;
          const libraryPom = (Array.isArray(productPomLibraries) ? productPomLibraries : []).find((pom) => {
            if (clonedItem.code && pom.code) return pom.code === clonedItem.code;
            if (clonedItem.id && pom.id) return String(pom.id) === String(clonedItem.id);
            return false;
          });
          const libraryGrading = libraryPom?.gradings?.find((g) => g.sizeName === newSizeName);
          const gradingValue = libraryGrading?.grading ?? '0';
          clonedItem.gradings.push({
            sizeName: newSizeName,
            grading: gradingValue,
            measurment: '0',
            isZero: isSampleSize || false,
            isSampleSize: isSampleSize || false,
          });
        }
      });
      clonedItem.gradings = reorderGradingsSafely(clonedItem.gradings, fullNames);
      return clonedItem;
    });
  }

  return { effectiveSelected, visibleNames, fullSizeData, fullNames, hiddenSizeNames, hiddenSizes, brandNewSizes, updatedSpecData };
};

/**
 * Reads the size names hidden for ONE grading group out of a stored hidden list.
 *
 * Entries carry the grading group that owns them, so a hide recorded against another group is
 * skipped rather than applied here. Legacy bare-string entries predate the stamp and belong to the
 * document they were loaded from, so they are always accepted.
 * @param {Array<{sizeName?: string, gradingGroupId?: string}|string>} hiddenSizes
 * @param {string} gradingGroupId Grading group whose hidden sizes are wanted.
 * @returns {string[]} Size names hidden for that grading group.
 */
export const hiddenSizeNamesForGroup = (hiddenSizes, gradingGroupId) => {
  const groupId = String(gradingGroupId ?? '');
  return (Array.isArray(hiddenSizes) ? hiddenSizes : [])
    .filter((entry) => {
      if (entry == null) return false;
      if (typeof entry !== 'object') return true;
      const entryGroupId = String(entry.gradingGroupId ?? '');
      return entryGroupId === '' || entryGroupId === groupId;
    })
    .map((entry) => (typeof entry === 'object' ? entry.sizeName : entry))
    .filter((sizeName) => sizeName != null && String(sizeName).trim() !== '');
};

/**
 * Collects the size options whose removal must be refused, as OPTIONS rather than as bare names.
 *
 * A base size belongs to ONE grading group. While this returned a flat name list, a two-group
 * order whose bases are `7YRS` and `8YRS` protected both names in both groups: the option was
 * greyed out in the group that had already moved its base, `onRemove` rejected it, and the hide
 * was never persisted (BM-9510). Every option already carries the base flag resolved for its own
 * group by `resolveProductSampleSizeName`, so that flag is the authority here.
 *
 * `localSizeRanges` and `persistedSampleSizeName` describe the grading group currently on screen
 * and are applied to it alone — they are the fallback for a base the freshly rebuilt options have
 * not caught up with, never a licence to protect the same name in a sibling group.
 * @param {Object} params
 * @param {Array<{label?: string, value?: string, gradingGroupId?: string, isSampleSize?: boolean}>} [params.sizeOptions] Every option the multiselect lists.
 * @param {string} [params.activeGroupId] The grading group whose tab is open.
 * @param {Array<{name?: string, isSampleSize?: boolean}>} [params.localSizeRanges] Active group's size list carrying isSampleSize flags.
 * @param {string|null} [params.persistedSampleSizeName] Base size persisted on the active group's document.
 * @returns {Array<Object>} Protected options, de-duplicated by grading-group-scoped key.
 */
export const collectProtectedSampleSizeOptions = ({ sizeOptions = [], activeGroupId = '', localSizeRanges = [], persistedSampleSizeName = null }) => {
  const options = Array.isArray(sizeOptions) ? sizeOptions : [];
  const groupId = String(activeGroupId ?? '').trim();
  const activeGroupNames = new Set(
    (Array.isArray(localSizeRanges) ? localSizeRanges : [])
      .filter((item) => item && typeof item === 'object' && item.isSampleSize && item.name != null && String(item.name).trim() !== '')
      .map((item) => String(item.name).trim()),
  );
  if (persistedSampleSizeName != null && String(persistedSampleSizeName).trim() !== '') {
    activeGroupNames.add(String(persistedSampleSizeName).trim());
  }

  const protectedOptions = options.filter((option) => {
    if (option?.isSampleSize === true) {
      return true;
    }
    if (groupId === '' || String(option?.gradingGroupId ?? '') !== groupId) {
      return false;
    }
    const sizeName = optionSizeName(option);
    return sizeName != null && activeGroupNames.has(String(sizeName).trim());
  });
  return uniqBy(protectedOptions, sizeOptionKey);
};

/**
 * Turns protected options into the key set every removal guard compares against.
 * @param {Array<Object>|null|undefined} protectedSampleSizeOptions
 * @returns {Set<string>} `<gradingGroupId>::<sizeName>` keys.
 */
export const protectedSampleSizeKeys = (protectedSampleSizeOptions) =>
  new Set((Array.isArray(protectedSampleSizeOptions) ? protectedSampleSizeOptions : []).map(sizeOptionKey));

/**
 * Reads the protected base size NAMES of one grading group, for the single-group derivations
 * (`deriveSizeVisibility`) that work in names because they never see more than one group.
 * @param {Array<Object>|null|undefined} protectedSampleSizeOptions
 * @param {string} gradingGroupId
 * @returns {string[]} De-duplicated names protected for that group.
 */
export const protectedSampleSizeNamesForGroup = (protectedSampleSizeOptions, gradingGroupId) => {
  const groupId = String(gradingGroupId ?? '').trim();
  const names = (Array.isArray(protectedSampleSizeOptions) ? protectedSampleSizeOptions : [])
    .filter((option) => String(option?.gradingGroupId ?? '').trim() === groupId)
    .map(optionSizeName)
    .filter((name) => name != null && String(name).trim() !== '');
  return uniqBy(names);
};

/**
 * Scopes a cross-group Size multiselect change down to a single active grading group. The Size
 * multiselect spans every selected grading group, so its raw selection must be split: the product
 * PUT keeps the FULL cross-group visible set (`productVisibleNames`) while the active group's
 * hide/show derivation must see ONLY its own sizes (`activeGroupSelected`) and its own sample sizes
 * (`activeGroupProtectedSampleSizes`) — otherwise another group's sizes leak in and pollute this
 * group's persisted size list.
 *
 * A deselected base size is re-added as the OPTION that owns it, matched by grading-group-scoped
 * key. Re-adding it by name took the first same-named option in any group, so unchecking a size in
 * one group could silently re-add its namesake into a sibling group (BM-9510).
 * @param {Object} params
 * @param {Array<{label?: string, value?: string, gradingGroupId?: string, isSampleSize?: boolean}>} params.selected Raw cross-group selection.
 * @param {string} params.activeGroupId The active tab's grading group id.
 * @param {{sizeRanges?: Array}|null} [params.matchingSizeRange] The active group's order grading group.
 * @param {Array<Object>} [params.protectedSampleSizeOptions] Protected base size options across all selected groups.
 * @returns {{activeGroupSizeNames: string[], productEffectiveSelected: Array, productVisibleNames: string[], activeGroupSelected: Array, activeGroupProtectedSampleSizes: string[]}}
 */
export const scopeSizeChangeToActiveGroup = ({ selected, activeGroupId, matchingSizeRange = null, protectedSampleSizeOptions = [] }) => {
  const groupId = String(activeGroupId ?? '');
  const options = Array.isArray(selected) ? selected : [];
  const protectedOptions = Array.isArray(protectedSampleSizeOptions) ? protectedSampleSizeOptions : [];

  const activeGroupSizeNames = Array.isArray(matchingSizeRange?.sizeRanges)
    ? matchingSizeRange.sizeRanges.map((res) => (typeof res === 'object' && res ? res.name : res)).filter(Boolean)
    : [];

  const selectedKeys = new Set(options.map(sizeOptionKey));
  const productProtectedOptions = protectedOptions.filter((opt) => !selectedKeys.has(sizeOptionKey(opt)));
  /**
   * De-duplicated by grading-group-scoped key, never by name: two grading groups may each own a
   * size of the same name, and collapsing them here would drop one group's size from the product
   * selection entirely.
   */
  const productEffectiveSelected = productProtectedOptions.length > 0 ? uniqBy([...options, ...productProtectedOptions], sizeOptionKey) : options;
  const productVisibleNames = uniqBy(productEffectiveSelected.map(optionSizeName));

  const activeGroupSelected = productEffectiveSelected.filter((opt) => {
    const optGroupId = String(opt?.gradingGroupId ?? '');
    return optGroupId === '' || optGroupId === groupId;
  });
  const activeGroupProtectedSampleSizes = protectedSampleSizeNamesForGroup(protectedOptions, groupId).filter((name) => activeGroupSizeNames.includes(name));

  return { activeGroupSizeNames, productEffectiveSelected, productVisibleNames, activeGroupSelected, activeGroupProtectedSampleSizes };
};

/**
 * Resolves the per-group sample size name for a single grading group, preferring
 * the freshest merged value (which includes local, not-yet-persisted overrides)
 * and falling back to the group's own stored value. Prevents the comma-joined
 * multi-group union (e.g. "S, M") from being written onto a per-group measurement
 * document, which corrupts the per-group base size that import/sample gates read.
 */
export const resolveGroupSampleSizeName = (mergedSampleSizeData, gradingGroupId, fallbackSampleSizeName) => {
  const groupId = String(gradingGroupId ?? '').trim();
  const rows = Array.isArray(mergedSampleSizeData) ? mergedSampleSizeData : [];
  const fromMerged = rows.find((row) => String(row?.gradingGroupId ?? '').trim() === groupId)?.sampleSizeName;
  const candidate = fromMerged != null && String(fromMerged).trim() !== '' ? fromMerged : fallbackSampleSizeName;
  const trimmed = candidate != null ? String(candidate).trim() : '';
  return trimmed !== '' ? trimmed : null;
};

/**
 * Normalizes a sample-size label for case/whitespace-insensitive comparison between
 * an import source's base size and the active spec's base size.
 * @param {unknown} value
 * @returns {string}
 */
export const normalizeSampleSizeToken = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

/**
 * Finds the grading a spec-table size column should display.
 *
 * Columns are rebuilt from the freshly saved `sizeRanges` the moment a size changes, while the rows
 * in state are still the ones loaded before that save. For one render the two disagree on
 * surrounding whitespace — a grading group owning `'X '` yields a column `'X '` over rows that still
 * say `'X'` — and an exact match found nothing, so the column rendered blank and editable on every
 * row until the next reload. Surrounding whitespace is therefore ignored when pairing the two.
 *
 * Case is not: `X` and `x` are two different sizes a grading group may legitimately own, so a column
 * is never paired with a grading whose name differs by case. A column with no grading of its own
 * still shows nothing — that is the honest answer, and better than showing another size's numbers.
 * @param {Array<{sizeName?: string}>|null|undefined} gradings A spec row's gradings.
 * @param {string|null|undefined} sizeName The column's size name.
 * @returns {{sizeName?: string}|undefined} The grading for that column, or undefined.
 */
export const findGradingForSize = (gradings, sizeName) => {
  const rows = Array.isArray(gradings) ? gradings : [];
  if (typeof sizeName !== 'string' || sizeName.trim() === '') {
    return undefined;
  }

  const exact = rows.find((grading) => grading?.sizeName === sizeName);
  if (exact) {
    return exact;
  }

  const target = sizeName.trim();
  return rows.find((grading) => typeof grading?.sizeName === 'string' && grading.sizeName.trim() === target);
};

/**
 * Picks the one size in a list that IS the given base size.
 *
 * Size vocabularies are case significant: a grading group legitimately carries `X` and `x` as two
 * different sizes, and only one of them is the base. Comparing through `normalizeSampleSizeToken`
 * alone made both of them match at once, which left two editable base columns on the spec table
 * and, because `calculationSampleSize` locates the base with `find`, derived every measurement off
 * whichever came first while the Sizes bar named the other. An exact, whitespace-trimmed match
 * therefore wins outright. The case fold BM-9510 added to tolerate stored label drift survives
 * only as a fallback, and only where it is unambiguous: exactly one candidate differing from the
 * target by case alone. Two candidates folding onto the same target mean the exact match was the
 * only defensible answer, and its absence means there is none.
 * @param {Array<{name?: string}|string|null|undefined>} sizeNames Candidate sizes, in render order.
 * @param {string|null|undefined} baseSizeName The base size label being matched.
 * @returns {number} Index of the base size, or -1 when no single size can be meant.
 */
export const resolveBaseSizeIndex = (sizeNames, baseSizeName) => {
  const labels = (Array.isArray(sizeNames) ? sizeNames : []).map((entry) => {
    const raw = entry && typeof entry === 'object' ? entry.name : entry;
    return typeof raw === 'string' ? raw.trim() : '';
  });
  const target = typeof baseSizeName === 'string' ? baseSizeName.trim() : '';
  if (target === '') {
    return -1;
  }

  const exactIndex = labels.indexOf(target);
  if (exactIndex >= 0) {
    return exactIndex;
  }

  const foldedTarget = target.toLowerCase();
  const foldedMatches = labels.reduce((matches, label, index) => (label.toLowerCase() === foldedTarget ? [...matches, index] : matches), []);
  return foldedMatches.length === 1 ? foldedMatches[0] : -1;
};

/**
 * The one answer to "which size is this grading group's base size", in precedence order.
 *
 * A base size can be named in three places, and they routinely disagree: the product's own
 * per-group record (`get-sample-size-name`), the measurement document it was saved on, and the
 * `isSampleSize` flag the grading group library ships with. The product outranks the library —
 * promoting a base size is a product level decision and the library is only ever the seed — so
 * a screen that reads the library flag directly shows and calculates against a size the product
 * has already moved past (BM-9510). Every caller resolves through here so the header, the size
 * options, the spec columns and a newly seeded POM row can never disagree about the base.
 *
 * The document name is only accepted for its OWN grading group: `sampleSizeName` has historically
 * been written as the comma-joined multi-group union ("S, M"), which belongs to no single group
 * and matches no size.
 * @param {Object} params
 * @param {Array<{gradingGroupId?: string, sampleSizeName?: string}>} [params.sampleSizeRows] Per-group product base sizes.
 * @param {string} params.gradingGroupId Grading group whose base size is wanted.
 * @param {string|null} [params.documentSampleSizeName] Base size persisted on the measurement document.
 * @param {string|null} [params.documentGradingGroupId] Group that document belongs to; unset means "this one".
 * @param {Array<{name?: string, isSampleSize?: boolean}|string>} [params.gradingGroupSizeRanges] Library size list.
 * @returns {string|null} The resolved base size name, or null when no source names one.
 */
export const resolveProductSampleSizeName = ({
  sampleSizeRows = [],
  gradingGroupId,
  documentSampleSizeName = null,
  documentGradingGroupId = null,
  gradingGroupSizeRanges = [],
}) => {
  const groupId = String(gradingGroupId ?? '').trim();
  const readName = (value) => (value != null && String(value).trim() !== '' ? String(value).trim() : null);

  const fromProductRow = readName(
    (Array.isArray(sampleSizeRows) ? sampleSizeRows : []).find((row) => String(row?.gradingGroupId ?? '').trim() === groupId)?.sampleSizeName,
  );
  if (fromProductRow) {
    return fromProductRow;
  }

  const documentGroupId = documentGradingGroupId != null ? String(documentGradingGroupId).trim() : '';
  if (documentGroupId === '' || documentGroupId === groupId) {
    const fromDocument = readName(documentSampleSizeName);
    if (fromDocument) {
      return fromDocument;
    }
  }

  const libraryBase = (Array.isArray(gradingGroupSizeRanges) ? gradingGroupSizeRanges : []).find((range) => typeof range === 'object' && range?.isSampleSize);
  return readName(libraryBase?.name);
};

/**
 * Builds the gradings of a POM row that is being added to the spec for the first time.
 *
 * The base flags are stamped from the resolved product base size rather than copied off the size
 * list's own `isSampleSize`, which carries the grading group library's base. Seeding from the
 * library gave a freshly added POM a different base row than the rows already on the spec, so its
 * measurements were derived from the wrong size (BM-9510). When no product base is known the
 * list's own flag still applies, which is the correct answer for a group that never moved.
 * @param {Array<{name?: string, isSampleSize?: boolean}|string>} sizeRanges Sizes the row needs, in render order.
 * @param {string|null|undefined} baseSizeName Resolved product base size.
 * @returns {Array<{sizeName: string, measurment: string, grading: string, isZero: boolean, isSampleSize: boolean}>}
 */
export const buildGradingsForSizes = (sizeRanges, baseSizeName) => {
  const named = (Array.isArray(sizeRanges) ? sizeRanges : [])
    .map((range) => ({
      sizeName: typeof range === 'object' && range ? range.name : range,
      libraryIsSampleSize: (typeof range === 'object' && range?.isSampleSize) || false,
    }))
    .filter((range) => range.sizeName != null && String(range.sizeName).trim() !== '');

  const hasProductBase = typeof baseSizeName === 'string' && baseSizeName.trim() !== '';
  const baseIndex = resolveBaseSizeIndex(
    named.map((range) => String(range.sizeName)),
    baseSizeName,
  );

  return named.map(({ sizeName, libraryIsSampleSize }, index) => {
    const isBase = hasProductBase ? index === baseIndex : libraryIsSampleSize;
    return { sizeName, measurment: '0', grading: '0', isZero: isBase, isSampleSize: isBase };
  });
};

/**
 * Reads the base/sample size name off a raw sizeRanges value (Grading Library or Spec
 * Template document shape: [{ name, isSampleSize }]). SpecTemplateLibraries.sizeRanges is
 * persisted as a JSON string (Mixed schema field, server/services/libraries/libraries.service.js:770-773),
 * while GradingsLibraries.sizeRanges is a real array — accepts either shape.
 * @param {Array|string|null|undefined} sourceSizeRanges
 * @returns {string}
 */
export const getSourceSampleSizeLabel = (sourceSizeRanges) => {
  let ranges = sourceSizeRanges;
  if (typeof ranges === 'string') {
    try {
      ranges = JSON.parse(ranges);
    } catch {
      return '';
    }
  }
  if (!Array.isArray(ranges)) return '';
  const baseEntry = ranges.find((range) => range?.isSampleSize);
  return typeof baseEntry?.name === 'string' ? baseEntry.name.trim() : '';
};

/**
 * Decides which POM codes should join the current selection when infinite-scroll appends more
 * rows: if every row visible before the append was already selected ("Select All" was active),
 * the newly-appended rows are selected too — otherwise scrolling further silently drops out of
 * an active select-all, since the header checkbox only ever snapshotted the rows loaded at click
 * time. Returns the codes to ADD (not the full resulting set); empty when select-all wasn't active.
 * @param {Array<{code?: string}>} prevSpecData Rows visible before the append.
 * @param {string[]} prevSelectedIds Currently selected codes.
 * @param {Array<{code?: string}>} newItems Rows newly appended by the scroll load.
 * @returns {string[]}
 */
export const expandSelectionForAppendedItems = (prevSpecData, prevSelectedIds, newItems) => {
  const visible = Array.isArray(prevSpecData) ? prevSpecData : [];
  const selected = Array.isArray(prevSelectedIds) ? prevSelectedIds : [];
  const wasSelectAllActive = visible.length > 0 && selected.length === visible.length;
  if (!wasSelectAllActive) return [];
  return (Array.isArray(newItems) ? newItems : []).map((item) => item?.code).filter(Boolean);
};

/**
 * Orchestrates a base/sample-size change: flushes any pending (debounced) spec edits to the
 * server FIRST, so the server-side grade-rule re-derive reads fresh measurements instead of a
 * stale DB copy. Without this, an edit made just before the size click races the 3s auto-save
 * debounce — the re-derive computes off stale data, persists it, and its response overwrites
 * the just-made local edit, leaving a stale grading sign in place (BM-9510).
 * @param {Object} params
 * @param {boolean} params.isDirty Whether there are unsaved spec edits (isSpecDirtyRef.current).
 * @param {boolean} params.hasSpecData Whether spec rows are currently loaded.
 * @param {string|null|undefined} params.productGradingId The loaded grading document's _id.
 * @param {() => Promise<any>} params.flush Flushes pending edits (performAutoSave).
 * @param {() => Promise<any>} params.changeSampleSize Performs the actual sample-size change.
 * @returns {Promise<any>} the changeSampleSize result.
 */
export const changeSampleSizeWithFlush = async ({ isDirty, hasSpecData, productGradingId, flush, changeSampleSize }) => {
  if (isDirty && hasSpecData && productGradingId) {
    await flush();
  }
  return changeSampleSize();
};

/**
 * Next sequence for a newly inserted POM row, assigned once at insert time so a
 * partially-loaded page can never renumber rows it hasn't loaded — unlike
 * reassigning `index + 1` for every row at save time.
 * @param {Array<{sequence?: number}>} rows
 * @returns {number}
 */
export const getNextSequence = (rows) => {
  const maxExisting = (rows || []).reduce((max, row) => Math.max(max, Number(row?.sequence) || 0), 0);
  return maxExisting + 1;
};

/**
 * Builds the size list a specification tab renders, taking it from the active tab's own
 * grading group so no other selected group's sizes appear. Entries arrive as `{name,
 * isSampleSize}` objects or bare name strings depending on the source, and unnamed entries
 * are dropped so a malformed size cannot render as a blank chip. The active group is also
 * the authority on which size is the sample size, keeping one group's sample flag off
 * another group's tab.
 * @param {{sizeRanges?: Array<{name?: string, isSampleSize?: boolean}|string>}|null|undefined} activeGradingGroup
 * @returns {Array<{name: string, isSampleSize: boolean}>}
 */
export const deriveActiveGroupSizeRanges = (activeGradingGroup) =>
  (Array.isArray(activeGradingGroup?.sizeRanges) ? activeGradingGroup.sizeRanges : [])
    .map((item) => ({
      name: typeof item === 'object' && item?.name ? item.name : typeof item === 'string' ? item : '',
      isSampleSize: typeof item === 'object' && item ? item.isSampleSize || false : false,
    }))
    .filter((item) => item.name !== '');

/**
 * Narrows a grading group's own size list down to the sizes its measurement document carries.
 *
 * The Sizes bar renders the grading group LIBRARY while the POM grid header renders the group's
 * persisted `sizeRanges`, so the two answer "which sizes does this tab have?" from different
 * places. A document narrower than its library therefore showed a size on the bar that the grid
 * had no column for. Scoping the bar to the document makes that impossible: it can
 * only ever offer a size the grid is rendering.
 *
 * An empty or absent document list is "not loaded yet", not "no sizes" — a tab switch updates the
 * active grading group before the incoming document arrives, and a brand-new tab has no document
 * at all — so the library stands until the document speaks. Names are matched verbatim, the way
 * the grid filters its own columns, because a grading group owns how its sizes are spelled.
 * @param {Array<{name: string, isSampleSize: boolean}>} groupSizeRanges Sizes the grading group owns.
 * @param {Array<{name?: string}|string>|null|undefined} documentSizeNames The tab's persisted size list.
 * @returns {Array<{name: string, isSampleSize: boolean}>} Sizes the tab may display, in library order.
 */
export const scopeGroupSizeRangesToDocument = (groupSizeRanges, documentSizeNames) => {
  const ranges = Array.isArray(groupSizeRanges) ? groupSizeRanges : [];
  const documentNames = sanitizeSizeNames(documentSizeNames);
  if (documentNames.length === 0) {
    return ranges;
  }
  const carried = new Set(documentNames.map((name) => String(name)));
  return ranges.filter((range) => carried.has(String(range?.name)));
};

/**
 * Rebases an imported POM row onto the product's own base/sample size.
 *
 * A POM imported from a Grading Library, Spec Template, another product, or the product's own
 * grading library carries the base size it was authored against. When the product's sample size
 * has since changed, that row's `isZero`/`isSampleSize` flags point at a size that is no longer
 * the base, which makes `calculationSampleSize`/`calculatePreviousSize` derive every measurement
 * off the wrong row. Rather than refusing the import (BM-9510), the row is re-pointed at the
 * product's base size: the flags move, and BOTH base rows — the product's new base and the
 * source's former base — are reset to `0` measurement and `0` grading, since neither value is
 * meaningful once the base moves. Every other size keeps the grading and measurement it was
 * imported with; the derived measurements are recomputed downstream by the calculation pass.
 * @param {{gradings?: Array<{sizeName?: string, measurment?: string, grading?: string}>}|null|undefined} pom Imported POM row.
 * @param {string|null|undefined} productSampleSizeName The product's current base size name.
 * @param {string|null|undefined} [sourceSampleSizeName] Source base size, used when the row carries no base flags.
 * @returns {Object|null|undefined} A new row, or the original when no rebase applies.
 */
export const rebaseImportedPomToProductSampleSize = (pom, productSampleSizeName, sourceSampleSizeName) => {
  if (!pom || !Array.isArray(pom.gradings) || typeof productSampleSizeName !== 'string' || productSampleSizeName.trim() === '') return pom;

  const sizeLabels = pom.gradings.map((grading) => grading?.sizeName);
  const productBaseIndex = resolveBaseSizeIndex(sizeLabels, productSampleSizeName);
  if (productBaseIndex < 0) return pom;

  const flaggedBase = pom.gradings.find((grading) => grading?.isZero || grading?.isSampleSize);
  const sourceBaseIndex = resolveBaseSizeIndex(sizeLabels, flaggedBase?.sizeName ?? sourceSampleSizeName);
  if (sourceBaseIndex === productBaseIndex) return pom;

  const gradings = pom.gradings.map((grading, index) => {
    if (index === productBaseIndex) {
      return { ...grading, measurment: '0', grading: '0', isZero: true, isSampleSize: true };
    }
    if (index === sourceBaseIndex) {
      return { ...grading, measurment: '0', grading: '0', isZero: false, isSampleSize: false };
    }
    return { ...grading, isZero: false, isSampleSize: false };
  });

  return { ...pom, gradings };
};

/**
 * Interprets the envelope returned by the sample-size-change endpoint.
 *
 * A success is asserted, never assumed. The endpoint used to answer a server-side failure
 * with HTTP 200 carrying `code: 400` in the body, so axios resolved and the thunk returned
 * `undefined`; the caller read that as "no rows to merge", kept the newly clicked base size
 * in local state and reported nothing — the base size appeared to move while nothing had
 * been persisted and no grade rule re-derived (BM-9510). An absent or non-200 envelope is a
 * failure, and an empty `rows` array on a 200 is a legitimate "nothing needed re-deriving".
 * @param {{code?: number, data?: Array}|null|undefined} updateResult The resolved thunk result.
 * @returns {{ok: boolean, rows: Array|null}}
 */
export const readSampleSizeChangeResult = (updateResult) => {
  if (!updateResult || updateResult.code !== 200) {
    return { ok: false, rows: null };
  }
  return { ok: true, rows: Array.isArray(updateResult.data) ? updateResult.data : null };
};

/**
 * Merges server re-derived gradings into the currently loaded POM rows, matched by `code`.
 *
 * Only `gradings` is replaced — every other field on the loaded row is local state the server
 * response does not speak for. Rows the server did not return, and rows carrying no `code` to
 * match on, are left exactly as they were rather than dropped. A server row that somehow
 * arrives with no base flag has its first grading stamped as the base so the forward
 * calculation cannot fall back to `sampleIndex = null` and derive every measurement from zero.
 * Returns new objects throughout; neither argument is mutated.
 * @param {Array<{code?: string}>|null|undefined} specData Currently loaded rows.
 * @param {Array<{code?: string, gradings?: Array}>|null|undefined} serverRows Re-derived rows.
 * @returns {Array} the merged rows.
 */
export const mergeReDerivedRows = (specData, serverRows) => {
  const rows = Array.isArray(specData) ? specData : [];
  const byCode = new Map((Array.isArray(serverRows) ? serverRows : []).filter((row) => row?.code).map((row) => [row.code, row]));
  if (byCode.size === 0) {
    return rows;
  }
  return rows.map((item) => {
    const serverRow = item?.code ? byCode.get(item.code) : null;
    if (!serverRow) {
      return item;
    }
    const gradings = Array.isArray(serverRow.gradings) ? serverRow.gradings.map((grading) => ({ ...grading })) : serverRow.gradings;
    if (Array.isArray(gradings) && gradings.length > 0 && !gradings.some((grading) => grading?.isSampleSize)) {
      gradings[0] = { ...gradings[0], isSampleSize: true, isZero: true };
    }
    return { ...item, gradings };
  });
};

/**
 * Derives the hidden size names for EVERY grading group the Size multiselect lists, from one
 * cross-group selection change.
 *
 * The Size multiselect spans all selected grading groups, but the hide/show derivation used to run
 * for the ACTIVE tab only — so unchecking a size that belongs to a grading group the user is not
 * currently viewing changed nothing: the group's hidden list was never recomputed, the option was
 * re-added to the selection on the very next render, and nothing was persisted for that group.
 * Switching to its tab still showed the size (BM-9510). Every listed group is derived here so a
 * deselect lands on the group that owns the size, whichever tab is open.
 *
 * A size name is unique only WITHIN a grading group, so groups are matched by `gradingGroupId` and
 * never by name — protection included. Protecting base sizes by bare name kept another group's
 * base visible here, so a group that had already moved its base could never hide the size it no
 * longer uses (BM-9510). A group's own base size can never be hidden, and a name already hidden
 * for a group but absent from the options (a custom size added on the order) is carried over
 * rather than silently un-hidden. Groups the multiselect does not list are left out entirely so
 * the caller's stored map keeps them untouched.
 * @param {Object} params
 * @param {Array<{label?: string, value?: string, gradingGroupId?: string}>} params.selected Currently selected options, across all groups.
 * @param {Array<{label?: string, value?: string, gradingGroupId?: string, isSampleSize?: boolean}>} params.sizeOptions Every size option the multiselect lists.
 * @param {Set<string>|string[]} [params.protectedSampleSizeKeys] Grading-group-scoped keys that must stay visible.
 * @param {Object<string, string[]>} [params.previousHiddenByGroup] Hidden names currently stored per group.
 * @returns {Object<string, string[]>} Hidden size names keyed by grading group id.
 */
export const deriveHiddenSizesByGroup = ({ selected, sizeOptions, protectedSampleSizeKeys: protectedKeys = [], previousHiddenByGroup = {} }) => {
  const options = Array.isArray(sizeOptions) ? sizeOptions : [];
  if (options.length === 0) {
    return {};
  }
  const protectedKeySet = protectedKeys instanceof Set ? protectedKeys : new Set((Array.isArray(protectedKeys) ? protectedKeys : []).filter(Boolean));
  const selectedKeys = new Set((Array.isArray(selected) ? selected : []).map(sizeOptionKey));

  const isOptionVisible = (option) => option?.isSampleSize === true || protectedKeySet.has(sizeOptionKey(option)) || selectedKeys.has(sizeOptionKey(option));

  const hiddenByGroup = new Map();
  const ownedNamesByGroup = new Map();
  options.forEach((option) => {
    const groupId = String(option?.gradingGroupId ?? '');
    const sizeName = optionSizeName(option);
    if (!groupId || !sizeName) {
      return;
    }
    if (!hiddenByGroup.has(groupId)) {
      hiddenByGroup.set(groupId, []);
      ownedNamesByGroup.set(groupId, new Set());
    }
    ownedNamesByGroup.get(groupId).add(sizeName);
    if (!isOptionVisible(option)) {
      hiddenByGroup.get(groupId).push(sizeName);
    }
  });

  const hiddenNamesByGroup = {};
  hiddenByGroup.forEach((hiddenNames, groupId) => {
    const ownedNames = ownedNamesByGroup.get(groupId);
    const carriedOver = (Array.isArray(previousHiddenByGroup?.[groupId]) ? previousHiddenByGroup[groupId] : []).filter(
      (sizeName) => sizeName && !ownedNames.has(sizeName) && !protectedKeySet.has(`${groupId}::${sizeName}`),
    );
    hiddenNamesByGroup[groupId] = uniqBy([...hiddenNames, ...carriedOver]);
  });
  return hiddenNamesByGroup;
};

/**
 * Resolves the hidden size names for the grading group currently on screen.
 *
 * The Sizes bar, the POM columns and the multiselect checkboxes must answer "is this size hidden?"
 * from ONE list. While the bar and the columns read the component's own `hiddenSizeNames` state and
 * the checkboxes read the per-group map, the two drifted apart: the state is reset from the server
 * document on every grading-group reload, so a reload landing after a hide put the size back on the
 * bar while its checkbox stayed clear (BM-9510). The map is authoritative; the local list is only
 * the fallback for the first render, before the map holds an entry for the group.
 * @param {Object} params
 * @param {Object<string, string[]>} [params.hiddenSizeNamesByGroup] Hidden names keyed by grading group id.
 * @param {string} [params.groupId] The grading group being rendered.
 * @param {string[]} [params.fallbackHiddenSizeNames] Local hide list, used only when the map has no entry.
 * @returns {string[]} Hidden size names for that grading group.
 */
export const resolveGroupHiddenSizeNames = ({ hiddenSizeNamesByGroup = {}, groupId = '', fallbackHiddenSizeNames = [] }) => {
  const fromMap = hiddenSizeNamesByGroup?.[String(groupId ?? '')];
  if (Array.isArray(fromMap)) {
    return fromMap;
  }
  return Array.isArray(fallbackHiddenSizeNames) ? fallbackHiddenSizeNames : [];
};

/**
 * Normalises one `sizeRanges` entry, which a grading group may hold either as `{ name, isSampleSize }`
 * or as a bare size-name string.
 * @param {{name?: string, isSampleSize?: boolean}|string|null} sizeRange
 * @returns {{name: string|undefined, isSampleSize: boolean}}
 */
const readSizeRangeEntry = (sizeRange) => {
  const isEntryObject = typeof sizeRange === 'object' && sizeRange !== null;
  return { name: isEntryObject ? sizeRange.name : sizeRange, isSampleSize: isEntryObject ? sizeRange.isSampleSize === true : false };
};

/**
 * Builds the size multiselect options for a set of grading groups, stamped with the group each
 * size belongs to.
 *
 * Every consumer of a selected size compares options by `sizeOptionKey`, i.e. by
 * `<gradingGroupId>::<name>`. The grading-group change handlers used to assemble their own option
 * list with no `gradingGroupId` on it and de-duplicate it by size NAME, so the options they handed
 * to the selector keyed as `::<name>` and matched no checkbox at all: adding a grading group left
 * the Sizes bar and the POM columns showing every size while the dropdown came back empty apart
 * from the protected sample size (BM-9510). Options are de-duplicated by that same group-scoped
 * key, so a name owned by two grading groups stays two separate options.
 * @param {Array<{_id?: string, value?: string, name?: string, sizeRanges?: Array<{name?: string, isSampleSize?: boolean}|string>}>} gradingGroups
 * @param {Array<string>} selectedGroupIds Ids of the grading groups to build options for.
 * @returns {Array<{label: string, value: string, optionKey: string, groupName: string, gradingGroupId: string, isSampleSize: boolean}>}
 */
export const buildGroupScopedSizeOptions = (gradingGroups, selectedGroupIds) => {
  const wantedGroupIds = new Set((Array.isArray(selectedGroupIds) ? selectedGroupIds : []).map((groupId) => String(groupId)));
  const selectedGroups = (Array.isArray(gradingGroups) ? gradingGroups : []).filter((gradingGroup) =>
    wantedGroupIds.has(String(gradingGroup?._id ?? gradingGroup?.value ?? '')),
  );

  const options = selectedGroups.flatMap((gradingGroup) => {
    const gradingGroupId = gradingGroup?._id ?? gradingGroup?.value;
    return (Array.isArray(gradingGroup?.sizeRanges) ? gradingGroup.sizeRanges : [])
      .map(readSizeRangeEntry)
      .filter((entry) => entry.name)
      .map((entry) => ({
        label: entry.name,
        value: entry.name,
        optionKey: `${String(gradingGroupId)}::${entry.name}`,
        groupName: gradingGroup.name,
        gradingGroupId,
        isSampleSize: entry.isSampleSize,
      }));
  });

  return uniqBy(options, 'optionKey');
};

/**
 * Decides which of the available size options stay selected after a grading group is added or
 * removed, keeping the answer scoped per grading group.
 *
 * A freshly added group takes the names the caller resolved for it — every size it owns, or the
 * subset inherited from a matching sibling group. Every already-selected group keeps exactly the
 * options it had checked, matched by group-scoped key, so adding a group that happens to own the
 * same size name cannot re-check a size the user had hidden on another group. The name list is
 * only the fallback for the first change, before any selection has been materialised.
 * @param {Object} params
 * @param {Array<Object>} [params.availableOptions] Group-scoped options for the groups now selected.
 * @param {Array<Object>} [params.previousSelectedSizes] The selection before this change.
 * @param {Array<string>} [params.addedGroupIds] Grading groups added by this change.
 * @param {Array<string>} [params.selectedSizeNames] Size names resolved for this change.
 * @returns {Array<Object>} The options that stay selected.
 */
export const scopeSelectionToGradingGroups = ({ availableOptions = [], previousSelectedSizes = [], addedGroupIds = [], selectedSizeNames = [] }) => {
  const options = Array.isArray(availableOptions) ? availableOptions : [];
  const addedIds = new Set((Array.isArray(addedGroupIds) ? addedGroupIds : []).map((groupId) => String(groupId)));
  const previousKeys = new Set((Array.isArray(previousSelectedSizes) ? previousSelectedSizes : []).map(sizeOptionKey));
  const wantedNames = new Set((Array.isArray(selectedSizeNames) ? selectedSizeNames : []).filter((sizeName) => sizeName));

  const scoped = options.filter((option) => {
    if (addedIds.has(String(option?.gradingGroupId ?? '')) || previousKeys.size === 0) {
      return wantedNames.size === 0 || wantedNames.has(optionSizeName(option));
    }
    return previousKeys.has(sizeOptionKey(option));
  });

  return scoped.length > 0 ? scoped : options;
};

/**
 * Stamps the `isZero` / `isSampleSize` pair onto the one grading that IS the given base size.
 *
 * The row is returned UNCHANGED when no single size can be meant — the name is absent, or it
 * matches none of this row's sizes (a POM added before the size joined the range, or the
 * comma-joined multi-group label). Stamping regardless left every grading flagged false, and
 * both forward calculations read the base off those flags: with none set they anchor on nothing
 * and rewrite the row's measurements from zero, so a row that merely lacked the base size had its
 * measurements destroyed with no error shown (BM-9510). A row that cannot be re-anchored keeps
 * the base it already has.
 * @param {{gradings?: Array<{sizeName?: string}>}|null|undefined} dataItem A POM row.
 * @param {string|null|undefined} baseSizeName The base size label to flag.
 * @returns {Object} The row with the base flags moved, or the row unchanged.
 */
export const stampBaseSizeFlags = (dataItem, baseSizeName) => {
  if (!dataItem || !Array.isArray(dataItem.gradings)) {
    return dataItem;
  }
  const baseIndex = resolveBaseSizeIndex(
    dataItem.gradings.map((grading) => grading?.sizeName),
    baseSizeName,
  );
  if (baseIndex < 0) {
    return dataItem;
  }
  return {
    ...dataItem,
    gradings: dataItem.gradings.map((grading, index) => ({
      ...grading,
      isZero: index === baseIndex,
      isSampleSize: index === baseIndex,
    })),
  };
};
